import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogContext";
import {
  ArrowLeft, Plus, Play, CheckCircle, XCircle, Trash, PencilSimple,
  Flag, ListChecks,
} from "@phosphor-icons/react";

const STATUS_META = {
  planning: { label: "Planning", color: "#6B7280", bg: "bg-slate-100 text-slate-700 border-slate-300" },
  active: { label: "Active", color: "#22C55E", bg: "bg-green-50 text-green-700 border-green-400" },
  completed: { label: "Completed", color: "#002FA7", bg: "bg-blue-50 text-blue-700 border-blue-300" },
  cancelled: { label: "Cancelled", color: "#FF3B30", bg: "bg-red-50 text-red-700 border-red-300" },
};

export default function Sprints() {
  const { id } = useParams();
  const { user } = useAuth();
  const { confirm } = useDialog();
  const [project, setProject] = useState(null);
  const [sprints, setSprints] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [error, setError] = useState("");
  const canManage = user.role !== "employee";

  const load = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/sprints`),
      ]);
      setProject(p.data);
      setSprints(s.data.sprints || []);
    } catch (e) {
      console.warn("Sprints load failed:", e?.response?.status || e?.message);
    }
  };

  useEffect(() => { load(); }, [id]);

  const startSprint = async (sid) => {
    setError("");
    try {
      await api.post(`/sprints/${sid}/start`);
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cancelSprint = async (sid) => {
    const ok = await confirm({
      title: "Cancel this sprint?",
      description: "All tasks currently in this sprint will move back to the backlog.",
      confirmText: "Cancel sprint",
      cancelText: "Keep sprint",
      variant: "destructive",
    });
    if (!ok) return;
    setError("");
    try {
      await api.post(`/sprints/${sid}/cancel`);
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteSprint = async (sid) => {
    const ok = await confirm({
      title: "Delete this sprint?",
      description: "Any tasks still in this sprint will return to the backlog. This cannot be undone.",
      confirmText: "Delete sprint",
      variant: "destructive",
    });
    if (!ok) return;
    setError("");
    try {
      await api.delete(`/sprints/${sid}`);
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!project) return <div className="p-12 text-sm text-muted-foreground">Loading…</div>;

  const groups = {
    active: sprints.filter(s => s.status === "active"),
    planning: sprints.filter(s => s.status === "planning"),
    completed: sprints.filter(s => s.status === "completed"),
    cancelled: sprints.filter(s => s.status === "cancelled"),
  };

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-6">
        <Link to={`/projects/${id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[#002FA7]" data-testid="back-to-kanban">
          <ArrowLeft size={14} weight="bold" /> Back to board
        </Link>
        <div className="flex items-end justify-between mt-3 gap-4 flex-wrap">
          <div>
            <div className="eyebrow mb-1">Sprints</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="sprints-title">
              {project.name} · Sprints
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Plan time-boxed sprints, drag tasks into them, and track velocity across your team.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/projects/${id}/backlog`} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="link-backlog">
              <ListChecks size={14} weight="bold" /> Backlog
            </Link>
            {canManage && (
              <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2" data-testid="btn-new-sprint">
                <Plus size={14} weight="bold" /> New sprint
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-8 lg:p-12 space-y-8 max-w-6xl">
        {error && <div className="border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] px-4 py-2 text-sm">{error}</div>}

        {sprints.length === 0 && (
          <div className="border border-border p-8 text-center text-sm text-muted-foreground">
            No sprints yet.{canManage && " Click 'New sprint' to create your first sprint."}
          </div>
        )}

        {["active", "planning", "completed", "cancelled"].map(group => {
          const items = groups[group];
          if (items.length === 0) return null;
          const meta = STATUS_META[group];
          return (
            <div key={group}>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2" style={{ background: meta.color }} />
                <div className="eyebrow" style={{ color: meta.color }}>{meta.label} ({items.length})</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map(s => (
                  <SprintCard
                    key={s.id}
                    sprint={s}
                    canManage={canManage}
                    onStart={() => startSprint(s.id)}
                    onComplete={() => setCompleting(s)}
                    onCancel={() => cancelSprint(s.id)}
                    onDelete={() => deleteSprint(s.id)}
                    onEdit={() => setEditing(s)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <SprintFormModal
          projectId={id}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {editing && (
        <SprintFormModal
          projectId={id}
          sprint={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {completing && (
        <CompleteSprintModal
          sprint={completing}
          allSprints={sprints}
          onClose={() => setCompleting(null)}
          onSaved={() => { setCompleting(null); load(); }}
        />
      )}
    </div>
  );
}

function SprintCard({ sprint, canManage, onStart, onComplete, onCancel, onDelete, onEdit }) {
  const meta = STATUS_META[sprint.status] || STATUS_META.planning;
  const pct = sprint.total_points > 0 ? Math.round((sprint.done_points / sprint.total_points) * 100) : 0;
  return (
    <div className="border border-border p-5 bg-white" data-testid={`sprint-${sprint.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-base truncate">{sprint.name}</div>
            <span className={`text-[10px] uppercase font-mono-mini border px-1.5 py-0.5 ${meta.bg}`}>{meta.label}</span>
          </div>
          {sprint.goal && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{sprint.goal}</div>}
          <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground font-mono-mini flex-wrap">
            {sprint.start_date && <span>{sprint.start_date}</span>}
            {sprint.end_date && <span>→ {sprint.end_date}</span>}
            <span>· {sprint.task_count || 0} tasks</span>
            <span>· {sprint.done_points || 0}/{sprint.total_points || 0} pts</span>
          </div>
        </div>
      </div>

      {sprint.total_points > 0 && (
        <div className="mt-3">
          <div className="h-1.5 bg-slate-100">
            <div className="h-full bg-[#002FA7]" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[10px] text-muted-foreground font-mono-mini mt-1">{pct}% complete</div>
        </div>
      )}

      {sprint.retrospective && (
        <div className="mt-3 text-xs text-muted-foreground italic border-l-2 border-border pl-3 line-clamp-3">
          <b className="not-italic">Retro:</b> {sprint.retrospective}
        </div>
      )}

      {canManage && (
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          {sprint.status === "planning" && (
            <>
              <button onClick={onStart} className="text-xs border border-green-500 text-green-700 px-2 py-1 hover:bg-green-50 flex items-center gap-1" data-testid={`start-sprint-${sprint.id}`}>
                <Play size={12} weight="bold" /> Start
              </button>
              <button onClick={onEdit} className="text-xs border border-border px-2 py-1 hover:bg-muted flex items-center gap-1">
                <PencilSimple size={12} weight="bold" /> Edit
              </button>
              <button onClick={onDelete} className="text-xs border border-red-400 text-red-700 px-2 py-1 hover:bg-red-50 flex items-center gap-1">
                <Trash size={12} weight="bold" /> Delete
              </button>
            </>
          )}
          {sprint.status === "active" && (
            <>
              <button onClick={onComplete} className="text-xs border border-[#002FA7] text-[#002FA7] px-2 py-1 hover:bg-blue-50 flex items-center gap-1" data-testid={`complete-sprint-${sprint.id}`}>
                <CheckCircle size={12} weight="bold" /> Complete
              </button>
              <button onClick={onEdit} className="text-xs border border-border px-2 py-1 hover:bg-muted flex items-center gap-1">
                <PencilSimple size={12} weight="bold" /> Edit
              </button>
              <button onClick={onCancel} className="text-xs border border-red-400 text-red-700 px-2 py-1 hover:bg-red-50 flex items-center gap-1">
                <XCircle size={12} weight="bold" /> Cancel
              </button>
            </>
          )}
          {(sprint.status === "completed" || sprint.status === "cancelled") && (
            <button onClick={onDelete} className="text-xs border border-red-400 text-red-700 px-2 py-1 hover:bg-red-50 flex items-center gap-1">
              <Trash size={12} weight="bold" /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SprintFormModal({ projectId, sprint, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: sprint?.name || "",
    goal: sprint?.goal || "",
    start_date: sprint?.start_date || "",
    end_date: sprint?.end_date || "",
    retrospective: sprint?.retrospective || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isEdit = !!sprint;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const body = { name: form.name, goal: form.goal, start_date: form.start_date, end_date: form.end_date };
      if (isEdit) body.retrospective = form.retrospective;
      if (isEdit) {
        await api.patch(`/sprints/${sprint.id}`, body);
      } else {
        await api.post(`/projects/${projectId}/sprints`, body);
      }
      onSaved();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white border border-border w-full max-w-lg p-8 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="sprint-form-dialog">
        <div className="eyebrow mb-2">{isEdit ? "Edit sprint" : "New sprint"}</div>
        <h2 className="font-display text-2xl font-bold mb-6">
          {isEdit ? sprint.name : "Plan a new sprint"}
        </h2>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="eyebrow block mb-2">Name</label>
            <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="sprint-name-input" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Goal</label>
            <textarea rows={2} value={form.goal} onChange={e => setForm({...form, goal: e.target.value})} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" placeholder="What outcome are we targeting?" data-testid="sprint-goal-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-2">Start date</label>
              <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="sprint-start-input" />
            </div>
            <div>
              <label className="eyebrow block mb-2">End date</label>
              <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="sprint-end-input" />
            </div>
          </div>
          {isEdit && (
            <div>
              <label className="eyebrow block mb-2">Retrospective notes</label>
              <textarea rows={3} value={form.retrospective} onChange={e => setForm({...form, retrospective: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" placeholder="What went well? What to improve?" />
            </div>
          )}
          {err && <div className="text-sm text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{err}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
            <button type="submit" disabled={busy} className="flex-1 btn-primary disabled:opacity-50" data-testid="submit-sprint">{busy ? "Saving…" : (isEdit ? "Save changes" : "Create sprint")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CompleteSprintModal({ sprint, allSprints, onClose, onSaved }) {
  const [action, setAction] = useState("backlog");
  const [targetSprintId, setTargetSprintId] = useState("");
  const [retro, setRetro] = useState(sprint.retrospective || "");
  const [velocity, setVelocity] = useState(sprint.done_points || 0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const planningSprints = allSprints.filter(s => s.status === "planning" && s.id !== sprint.id);

  const submit = async (e) => {
    e.preventDefault();
    if (action === "specific" && !targetSprintId) { setErr("Choose a target sprint"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/sprints/${sprint.id}/complete`, {
        unfinished_action: action,
        target_sprint_id: action === "specific" ? targetSprintId : "",
        retrospective: retro,
        velocity_points: Number(velocity) || 0,
      });
      onSaved();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white border border-border w-full max-w-lg p-8 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="complete-sprint-dialog">
        <div className="eyebrow mb-2">Complete sprint</div>
        <h2 className="font-display text-2xl font-bold mb-4">{sprint.name}</h2>
        <p className="text-sm text-muted-foreground mb-6">
          {sprint.done_count}/{sprint.task_count} tasks done · {sprint.done_points}/{sprint.total_points} points delivered.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="eyebrow block mb-2">Move unfinished tasks to…</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={action === "backlog"} onChange={() => setAction("backlog")} data-testid="unfinished-backlog" />
                Backlog
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={action === "next"} onChange={() => setAction("next")} data-testid="unfinished-next" />
                The next planning sprint (auto-picked)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={action === "specific"} onChange={() => setAction("specific")} data-testid="unfinished-specific" />
                A specific sprint…
              </label>
              {action === "specific" && (
                <select value={targetSprintId} onChange={e => setTargetSprintId(e.target.value)} className="ml-6 border border-border px-3 py-2 text-sm bg-white" data-testid="target-sprint-select">
                  <option value="">Choose a planning sprint…</option>
                  {planningSprints.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-2">Velocity (points)</label>
              <input type="number" min="0" value={velocity} onChange={e => setVelocity(e.target.value)} className="w-full border border-border px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2">Retrospective</label>
            <textarea rows={3} value={retro} onChange={e => setRetro(e.target.value)} className="w-full border border-border px-3 py-2 text-sm" placeholder="What went well? What to improve?" data-testid="retro-input" />
          </div>
          {err && <div className="text-sm text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{err}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
            <button type="submit" disabled={busy} className="flex-1 btn-primary disabled:opacity-50" data-testid="submit-complete-sprint">{busy ? "Completing…" : "Complete sprint"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
